'use client';

import { useEffect, useRef, useState } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import { motion } from 'framer-motion';
import { Info } from 'lucide-react';
import { useHandleError } from '@/hooks/useHandleError';
import { useSafeNavigation } from '@/hooks/useSafeNavigation';
import { apiFetch } from '@/lib/api/apiClient';
import { useAuthStore } from '@/stores/useAuthStore';
import { useFestaEntryStore } from '@/stores/useFestaEntryStore';
import {
    FestaEntryFestaEntryTypeItem,
    FestaEntryItem,
    FestaEntryListResponse,
    FestaEntryLocationItem,
    FestaEntryNoteItem,
    FestaEntryOptionResponse,
    FestaEntryOrganizationItem,
    FestaEntryOrganizationListResponse,
    FestaEntryScheduleItem,
    FestaEntryThanksItem,
    FestaEntryUserResponse,
    Step,
    UserFestaEntryApplyResponse,
    UserFestaEntryFormData,
    UserItem,
} from '@/types/festaEntries';
import IconAdaptive from '@/components/IconAdaptive';
import ImageAdaptive from '@/components/ImageAdaptive';
import { Birthdate } from '@/components/ui/form/FormSelectBirthdate';
import FormFestaEntry from '@/(routers)/(entry)/festa-entry/form/components/FormFestaEntry';
import FormUserInfo from '@/(routers)/(entry)/festa-entry/form/components/FormUserInfo';

export default function Form() {
    const { handleError } = useHandleError();
    const { safeReplace } = useSafeNavigation();
    const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
    const setThanksInfo = useFestaEntryStore((s) => s.setThanksInfo);
    const draft = useFestaEntryStore((s) => s.draft);
    const setStep = useFestaEntryStore((s) => s.setStep);
    const clearDraft = useFestaEntryStore((s) => s.clearDraft);
    const step = useFestaEntryStore((s) => s.step);
    const validationRef = useRef<HTMLDivElement>(null);
    const {
        handleError: submitHandleError,
        clearError: submitClearError,
        errorMessage: submitErrorMessage,
    } = useHandleError(validationRef);
    const [isProcessing, setIsProcessing] = useState(false);
    const pathname = usePathname();
    const searchParams = useSearchParams();
    const tail = (() => {
        const marker = '/festa-entry/form/';
        const i = pathname.indexOf(marker);
        return i >= 0 ? pathname.slice(i + marker.length) : '';
    })();
    const qs = new URLSearchParams([tail, searchParams.toString()].filter(Boolean).join('&'));
    const numOrUndefined = (v: string | null) => (v && /^\d+$/.test(v) ? Number(v) : undefined);
    const urlFestaEntryId = numOrUndefined(qs.get('id'));
    const deepLink = {
        schedule: numOrUndefined(qs.get('schedule')),
        location: numOrUndefined(qs.get('location')),
        type: numOrUndefined(qs.get('type')),
    } as const;
    const [isDeepLinkedStart, setIsDeepLinkedStart] = useState(false);
    const [festaEntryList, setFestaEntryList] = useState<FestaEntryItem[]>([]);
    const [isFestaEmpty, setIsFestaEmpty] = useState(false);

    const [festaEntryFestaEntryTypeList, setFestaEntryFestaEntryTypeList] = useState<FestaEntryFestaEntryTypeItem[]>(
        [],
    );
    const [festaEntryScheduleList, setFestaEntryScheduleList] = useState<FestaEntryScheduleItem[]>([]);
    const [festaEntryLocationList, setFestaEntryLocationList] = useState<FestaEntryLocationItem[]>([]);
    const [festaEntryNoteList, setFestaEntryNoteList] = useState<FestaEntryNoteItem[]>([]);
    const [festaEntryOrganizationList, setFestaEntryOrganizationList] = useState<FestaEntryOrganizationItem[]>([]);

    const FESTA_MARKERS = 2;

    const [formData, setFormData] = useState<UserFestaEntryFormData>({
        festa_entry_id: 0,
        festa_entry_name: '',
        festa_entry_contact_email: '',
        festa_entry_contact_name: '',
        schedule_group_id: 0,
        schedule_labels: [],
        location_id: 0,
        location_name: '',
        festa_entry_type_id: 0,
        festa_entry_type_cd: '',
        festa_entry_type_name: '',
        schoomyers_id: null,
        name: '',
        name_kana: '',
        email: '',
        phone_number: '',
        birthday: '',
        gender_cd: '',
        gender_name: '',
        remarks: '',
        entry_organization_cd: '',
        entry_organization_name: '',
        eyecatch_public_url: '',
    });

    const [birthdate, setBirthdate] = useState<Birthdate>({
        year: '',
        month: '',
        day: '',
    });

    const INITIAL_FORM_DATA: UserFestaEntryFormData = {
        festa_entry_id: 0,
        festa_entry_name: '',
        festa_entry_contact_email: '',
        festa_entry_contact_name: '',
        schedule_group_id: 0,
        schedule_labels: [],
        location_id: 0,
        location_name: '',
        festa_entry_type_id: 0,
        festa_entry_type_cd: '',
        festa_entry_type_name: '',
        festa_entry_type_is_login_required: 0,
        schoomyers_id: null,
        name: '',
        name_kana: '',
        email: '',
        phone_number: '',
        birthday: '',
        gender_cd: '',
        gender_name: '',
        remarks: '',
        entry_organization_cd: '',
        entry_organization_name: '',
        eyecatch_public_url: '',
    };

    const handleChangeSelectedFesta = async (id: number) => {
        if (id > 0) {
            const option = await fetchFestaEntryOptionList(id);
            setFestaEntryFestaEntryTypeList(option.festa_entry_festa_entry_types);
            setFestaEntryScheduleList(option.festa_entry_schedules);
            setFestaEntryLocationList(option.festa_entry_locations);
            setFestaEntryNoteList(option.festa_entry_notes);
        } else {
            setFestaEntryFestaEntryTypeList([]);
            setFestaEntryScheduleList([]);
            setFestaEntryLocationList([]);
            setFestaEntryNoteList([]);
        }
    };

    const fetchFestaEntryList = async () => {
        try {
            const festaEntryList: FestaEntryListResponse = await apiFetch(`/entry/list`, { method: 'GET' });
            return festaEntryList.festa_entries ?? [];
        } catch (error: unknown) {
            handleError(error);
            return [];
        }
    };

    const fetchFestaEntryOptionList = async (festaEntryId: number) => {
        try {
            const res: FestaEntryOptionResponse = await apiFetch(
                `/entry/options?festa_entry_id=${encodeURIComponent(festaEntryId)}`,
                { method: 'GET' },
            );

            const types: FestaEntryFestaEntryTypeItem[] = (res.entry_types ?? []).map((t) => ({
                id: t.id,
                cd: t.cd,
                festa_entry_id: festaEntryId,
                festa_entry_type_cd: t.cd ?? '',
                name: t.name,
                is_login_required: t.is_login_required,
            }));

            const schedules: FestaEntryScheduleItem[] = (res.entry_schedules ?? []).map((s) => ({
                schedule_group_id: s.schedule_group_id,
                festa_entry_id: s.festa_entry_id,
                schedule_labels: s.schedule_labels ?? [],
                entry_token: s.entry_token,
            }));

            const locations: FestaEntryLocationItem[] = (res.entry_locations ?? []).map((l) => ({
                id: l.id,
                festa_entry_id: l.festa_entry_id,
                is_online: l.is_online,
                name: l.name,
                zip: l.zip,
                prefecture_cd: l.prefecture_cd,
                prefecture_name: l.prefecture_name,
                address_1: l.address_1,
                address_2: l.address_2,
            }));

            const notes: FestaEntryNoteItem[] = (res.entry_notes ?? []).map((n, i) => ({
                id: n.id ?? i,
                festa_entry_id: n.festa_entry_id ?? festaEntryId,
                title: n.title ?? '',
                description: n.description ?? '',
            }));

            return {
                festa_entry_festa_entry_types: types,
                festa_entry_schedules: schedules,
                festa_entry_locations: locations,
                festa_entry_notes: notes,
            };
        } catch (error: unknown) {
            handleError(error);
            return {
                festa_entry_festa_entry_types: [],
                festa_entry_schedules: [],
                festa_entry_locations: [],
                festa_entry_notes: [],
            };
        }
    };

    const fetchUserProfile = async (isLoginRequired: number) => {
        if (!isAuthenticated) return;
        try {
            if (isLoginRequired === 1) {
                const res: FestaEntryUserResponse = await apiFetch('/entry/user');
                const user: UserItem = res.festa_entry_user;
                setFormData((prev) => ({
                    ...prev,
                    schoomyers_id: user.schoomyers_id ?? prev.schoomyers_id,
                    name: user.name ?? prev.name,
                    name_kana: user.name_kana ?? prev.name_kana,
                    email: user.email ?? prev.email,
                    phone_number: user.phone_number ?? prev.phone_number,
                    birthday: user.birthday ?? prev.birthday,
                    gender_cd: user.gender_cd ?? prev.gender_cd,
                }));
                if (user.birthday) {
                    setBirthdate({
                        year: user.birthday.slice(0, 4),
                        month: user.birthday.slice(4, 6),
                        day: user.birthday.slice(6),
                    });
                }
            } else {
                setFormData((prev) => ({
                    ...prev,
                    schoomyers_id: null,
                    name: '',
                    name_kana: '',
                    email: '',
                    phone_number: '',
                    birthday: '',
                    gender_cd: '',
                }));
                setBirthdate({
                    year: '',
                    month: '',
                    day: '',
                });
            }
        } catch (error: unknown) {
            handleError(error);
            return [];
        }
    };

    const fetchFestaEntryOrganizationList = async () => {
        try {
            const res: FestaEntryOrganizationListResponse = await apiFetch(`/entry/organizations`, {
                method: 'GET',
            });
            return res.festa_entry_organizations ?? [];
        } catch (error: unknown) {
            handleError(error);
            return [];
        }
    };

    const refreshAuth = async () => {
        try {
            const auth = await apiFetch(`/auth/is_authenticated`, { method: 'GET' });
            const isAuthedServer = !!auth.is_authenticated_server;
            return isAuthedServer;
        } catch (e) {
            return false;
        }
    };

    useEffect(() => {
        const init = async () => {
            try {
                setStep(null);
                // ログイン判定
                const isAuthenticatedServer = await refreshAuth();

                // マスタ取得
                const festa = await fetchFestaEntryList();
                const organizations = await fetchFestaEntryOrganizationList();
                setFestaEntryList(festa);
                setFestaEntryOrganizationList(organizations);

                if (!festa || festa.length === 0) {
                    setIsFestaEmpty(true);
                    return;
                }

                // フェスタ初期値
                const initialId =
                    urlFestaEntryId && festa.some((f) => f.festa_entry_id === urlFestaEntryId) ? urlFestaEntryId : 0;

                let currentId = 0;
                let isloginRequired = 0;
                let nextStep: Step = 'festa';

                // URLにidがあったら
                if (initialId > 0) {
                    const hit = festa.find((f) => f.festa_entry_id === initialId)!;
                    currentId = hit.festa_entry_id;
                    setFormData((prev) => ({
                        ...prev,
                        festa_entry_id: hit.festa_entry_id,
                        festa_entry_name: hit.name,
                        festa_entry_contact_email: hit.contact_email ?? '',
                        festa_entry_contact_name: hit.contact_name ?? '',
                        eyecatch_public_url: hit.eyecatch_public_url ?? '',
                    }));

                    // 選択されたフェスタのオプション取得
                    const option = await fetchFestaEntryOptionList(hit.festa_entry_id);
                    setFestaEntryFestaEntryTypeList(option.festa_entry_festa_entry_types);
                    setFestaEntryScheduleList(option.festa_entry_schedules);
                    setFestaEntryLocationList(option.festa_entry_locations);
                    setFestaEntryNoteList(option.festa_entry_notes);

                    // ディープリンクがそろっていたらuserステップへ
                    const scheduleHit = option.festa_entry_schedules.find(
                        (s) => s.schedule_group_id === deepLink.schedule,
                    );
                    const locationHit = option.festa_entry_locations.find((l) => l.id === deepLink.location);
                    const typeHit = option.festa_entry_festa_entry_types.find((t) => t.id === deepLink.type);
                    const allProvided = !!(scheduleHit && locationHit && typeHit);

                    if (allProvided) {
                        // ログイン済み、またはログイン不要ならuserステップへ
                        setFormData((prev) => ({
                            ...prev,
                            schedule_group_id: scheduleHit!.schedule_group_id,
                            schedule_labels: scheduleHit!.schedule_labels ?? [],
                            location_id: locationHit!.id,
                            location_name: locationHit!.name ?? '',
                            festa_entry_type_id: typeHit!.id,
                            festa_entry_type_cd: typeHit!.festa_entry_type_cd ?? '',
                            festa_entry_type_name: typeHit!.name ?? '',
                            festa_entry_type_is_login_required: typeHit!.is_login_required ?? 0,
                        }));
                        nextStep = 'user';
                        setIsDeepLinkedStart(true);
                        isloginRequired = typeHit!.is_login_required ?? 0;
                    }
                }
                // リダイレクト時はドラフト復元しuserステップへ
                if (draft) {
                    if (draft.festa_entry_id && draft.festa_entry_id !== currentId) {
                        const optionDraft = await fetchFestaEntryOptionList(draft.festa_entry_id);
                        setFestaEntryFestaEntryTypeList(optionDraft.festa_entry_festa_entry_types);
                        setFestaEntryScheduleList(optionDraft.festa_entry_schedules);
                        setFestaEntryLocationList(optionDraft.festa_entry_locations);
                        setFestaEntryNoteList(optionDraft.festa_entry_notes);
                    }
                    setFormData((prev) => ({ ...prev, ...draft }));
                    const ready =
                        (draft.festa_entry_id ?? 0) > 0 &&
                        (draft.schedule_group_id ?? 0) > 0 &&
                        (draft.location_id ?? 0) > 0 &&
                        !!(draft.festa_entry_type_cd && draft.festa_entry_type_cd.trim());

                    isloginRequired = Number(draft.festa_entry_type_is_login_required ?? 0);

                    if (isloginRequired) {
                        if (isAuthenticatedServer) {
                            nextStep = ready ? 'user' : 'festa';
                        } else {
                            nextStep = 'festa';
                        }
                    } else {
                        nextStep = 'user';
                    }
                    clearDraft();
                }

                // ログイン時プロフィール取得
                await fetchUserProfile(isloginRequired);
                setStep(nextStep);
            } catch (e) {
                handleError(e);
            }
        };

        init();
        return;
    }, []);

    const handleUserFestaEntrySubmit = async () => {
        submitClearError();
        try {
            setIsProcessing(true);
            const payload = {
                ...formData,
                entry_organization_cd: formData.entry_organization_cd === 'other' ? '' : formData.entry_organization_cd,
            };
            const res: UserFestaEntryApplyResponse = await apiFetch(`/entry`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload),
            });
            const entryDateTime = res.entry_date_time;
            const festaInfo: FestaEntryThanksItem = {
                festa_entry_name: formData.festa_entry_name ?? '',
                schedule_labels: formData.schedule_labels ?? [],
                location_name: formData.location_name ?? '',
                festa_entry_type_name: formData.festa_entry_type_name ?? '',
                entry_date_time: entryDateTime,
                email: formData.email,
            };
            setThanksInfo(festaInfo);
            clearDraft();
            safeReplace(`/festa-entry/form/thanks`);
        } catch (error: unknown) {
            submitHandleError(error);
            setIsProcessing(false);
        }
    };

    const goNextToUserInfo = async () => {
        if (formData) {
            await fetchUserProfile(formData.festa_entry_type_is_login_required ?? 0);
        }
        clearDraft();
        setStep('user');
        if (typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const goBackToFesta = async () => {
        setFormData(INITIAL_FORM_DATA);

        setBirthdate({ year: '', month: '', day: '' });
        setFestaEntryFestaEntryTypeList([]);
        setFestaEntryScheduleList([]);
        setFestaEntryLocationList([]);
        setFestaEntryNoteList([]);
        submitClearError();

        clearDraft();
        setStep('festa');

        await refreshAuth();
        if (typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    return (
        <div className="flex flex-col justify-start items-center w-full font-notojp font-color-black-base">
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5, delay: 0.0 }}
                className="flex flex-col w-full xl:w-[90%] 2xl:w-[80%] pb-12"
            >
                <div className="flex justify-start items-center font-corp font-bold text-3xl p-4 min-h-[60px] md:min-h-[75px]">
                    <ImageAdaptive src="/images/common/schoomy.png" baseWidth={150} breakpoints={{ md: 180 }} />
                </div>
                <div className="flex justify-center items-start w-full pt-2 pb-2 font-montserrat font-black text-color-base-2 text-[40px] xl:text-[60px] tracking-widest">
                    ENTRY
                </div>
                <div className="flex justify-center items-start w-full pb-12 font-bold text-gray-400 text-xl xl:text-[25px] tracking-widest">
                    フェスタ参加申し込み
                </div>
                {step === 'festa' || isFestaEmpty ? (
                    <motion.div
                        initial={{ y: -50, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{
                            type: 'spring',
                            stiffness: 200,
                            damping: 10,
                            delay: 0.5,
                        }}
                        className="flex justify-center items-center min-h-[120px] md:min-h-[150px]"
                    >
                        <ImageAdaptive src={`/images/festa-entry/top.svg`} baseWidth={100} breakpoints={{ md: 140 }} />
                    </motion.div>
                ) : step === 'user' ? (
                    <>
                        <div className="flex justify-center">
                            <motion.img
                                src={
                                    formData?.eyecatch_public_url && formData.eyecatch_public_url !== 'null'
                                        ? formData.eyecatch_public_url
                                        : '/images/festa-entry/eyecatch_default.png'
                                }
                                variants={{ initial: { scale: 1 }, hovered: { scale: 1.01 } }}
                                transition={{ type: 'spring', stiffness: 300 }}
                                className="w-[80%] xl:w-[60%] h-full rounded-xl"
                            />
                        </div>
                    </>
                ) : null}
            </motion.div>
            {isFestaEmpty ? (
                <div className="flex items-center justify-center w-full py-16 gap-4 ">
                    <IconAdaptive
                        Icon={Info}
                        strokeWidth={2.0}
                        baseSize={24}
                        breakpoints={{ md: 32 }}
                        className="mr-[1px] text-gray-500"
                    />
                    <div className="text-sm xl:text-base text-gray-600">現在参加申し込み可能なフェスタはありません</div>
                </div>
            ) : (
                <>
                    {step === 'festa' ? (
                        <FormFestaEntry
                            refreshAuth={refreshAuth}
                            formData={formData}
                            setFormData={setFormData}
                            onNext={goNextToUserInfo}
                            itemStart={1}
                            festaEntryList={festaEntryList}
                            festaEntryTypes={festaEntryFestaEntryTypeList}
                            festaEntryScheduleList={festaEntryScheduleList}
                            festaEntryLocationList={festaEntryLocationList}
                            onChangeSelectedFestaEntryId={handleChangeSelectedFesta}
                            isProcessing={isProcessing}
                        />
                    ) : step === 'user' ? (
                        <FormUserInfo
                            formData={formData}
                            setFormData={setFormData}
                            birthdate={birthdate}
                            setBirthdate={setBirthdate}
                            onBack={goBackToFesta}
                            itemStart={isDeepLinkedStart ? 1 : 1 + FESTA_MARKERS}
                            festaEntryNoteList={festaEntryNoteList}
                            festaEntryOrganizationList={festaEntryOrganizationList}
                            onSubmit={handleUserFestaEntrySubmit}
                            isProcessing={isProcessing}
                            isDeepLinkedStart={isDeepLinkedStart}
                            validationRef={validationRef}
                            errorMessage={submitErrorMessage}
                        />
                    ) : null}
                </>
            )}
        </div>
    );
}
